import { projectAtom, projectItemToProjectInfo } from "../project/projects-state"
import { pushDialog } from "../dialogs-state"
import { chooseStyle, clearProject } from "../project/project"
import { setUpProject } from "../project/setup-project"
import { LoadingDialog } from "@/components/Dialogs/LoadingDialog"
import { previewImporter } from "../importing/preview-importing"
import { processingQueue } from "../processing/processing-queue"
import { popActiveLoadingDialogs } from "@/store/loading-dialog-utils"
import { store } from "../store"
import type { SelectStyleOptions } from "./style-state"
import { isLocalStyle } from "./style-state"
import { isNetworkConnectivityError, showActiveNetworkErrorToast } from "@/api/network-behavior"
import { CatalogManager } from "@/lib/catalog"
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"

export async function switchStyle() {
	const currentProject = store.get(projectAtom)
	const currentFolder = currentProject?.handle
	if (currentProject && currentFolder) {
		let isSetupInFlight = false
		const handleSwitchStyleError = (error: unknown) => {
			console.error("Failed to switch style", error)
			popActiveLoadingDialogs()
			if (isNetworkConnectivityError(error)) {
				showActiveNetworkErrorToast()
			} else {
				pushDialog(ConfirmDialog, {
					title: i18n.t("store:switchStyle.failedSwitchTitle"),
					message: i18n.t("store:switchStyle.tryAgainLater"),
					primary: { title: i18n.t("common:action.ok") },
					trackingViewName: "switch_style_failure_dialog",
				})
			}
			void processingQueue.resumeQueue()
			void previewImporter.resumeIfNeeded()
		}

		const runStyleSwitchSetup = async (options?: SelectStyleOptions) => {
			if (isSetupInFlight) {
				return
			}
			isSetupInFlight = true
			try {
				let selectedOptions = options
				if (!selectedOptions) {
					// Pause queues before entering style-switch dialog flow.
					await processingQueue.pauseQueue()
					await previewImporter.pause()
					selectedOptions = await chooseStyle({
						disableClose: false,
						isSwitching: true,
						onReselect: (reselectedOptions) => {
							// Allow retry from the same style-selection/preview flow after a failed first apply.
							void runStyleSwitchSetup(reselectedOptions)
						},
						onAllDialogsClosed: () => {
							// Resume queues if user cancels style switch or after style apply flow exits.
							void processingQueue.resumeQueue()
							void previewImporter.resumeIfNeeded()
						},
					})
				}

				// Keep StylePreviewDialog on stack so failed setup can return user to preview for retry.
				pushDialog(LoadingDialog, { title: i18n.t("store:switchStyle.switchingAiStyle") })

				const projectInfo = projectItemToProjectInfo(currentProject)
				await clearProject(projectInfo, ["edits"])
				if (selectedOptions.publishedReferences) {
					const selectedStyle = selectedOptions.style
					if (
						selectedStyle !== "new" &&
						selectedStyle !== "manual" &&
						isLocalStyle(selectedStyle)
					) {
						await CatalogManager.storeStyleInfo(selectedStyle)
						await CatalogManager.replaceStyleReferences(
							selectedOptions.publishedReferences,
						)
					}
				}

				await setUpProject({
					handle: currentFolder,
					aiStyleTriggerSource: "switch_ai_style",
					selectedStyle: selectedOptions.style,
					needsSetup: selectedOptions.needsSetup,
					isExpressSetup: selectedOptions.isExpressSetup,
					existingProject: currentProject,
					useCurrentSessionUI: true,
				})
			} catch (error) {
				handleSwitchStyleError(error)
			} finally {
				isSetupInFlight = false
			}
		}

		await runStyleSwitchSetup()
	}
}
